/**
 * edicaoApi — chamadas à API de Correção da Base (M8) sobre o ApiClient base.
 *
 * Contrato congelado (BINDING):
 *  - PATCH /visitas/{id}           req { row_version, campos[] }  res (nível superior)
 *  - POST  /visitas/{id}/conferir  req { row_version }           res (nível superior)
 *  - GET   /visitas/{id}/historico res { data: [...] }
 *
 * O 409 (conflito) é propagado como ConflictError, carregando o `estadoAtual`
 * lido do corpo de erro (ApiError.body.error.details.estadoAtual). Nenhum dado
 * pessoal é logado (RN-M11-001).
 */

import { ApiClient, ApiError } from './apiClient';
import {
  ConferirResposta,
  EditarVisitaRequest,
  HistoricoEntry,
  VisitaAtualizada,
} from './edicao-types';

/** Erro de conflito de concorrência (HTTP 409) com o estado atual da visita. */
export class ConflictError extends Error {
  constructor(public readonly estadoAtual: VisitaAtualizada | null) {
    super('A visita foi alterada por outro usuário.');
    this.name = 'ConflictError';
  }
}

/** Erro de dependência indisponível (HTTP 503) — ex.: motor de validação. */
export class ServiceUnavailableError extends Error {
  constructor() {
    super('Serviço temporariamente indisponível. Tente novamente.');
    this.name = 'ServiceUnavailableError';
  }
}

interface ErroCorpo {
  readonly error?: { readonly details?: { readonly estadoAtual?: VisitaAtualizada } };
}

function extrairEstadoAtual(body: unknown): VisitaAtualizada | null {
  const corpo = body as ErroCorpo | undefined;
  return corpo?.error?.details?.estadoAtual ?? null;
}

export class EdicaoApi {
  constructor(private readonly client: ApiClient) {}

  /** PATCH /visitas/{id} — salva a edição; 409 → ConflictError; 503 → ServiceUnavailableError. */
  async editar(id: string, req: EditarVisitaRequest): Promise<VisitaAtualizada> {
    try {
      const resp = await this.client.request<{ data: VisitaAtualizada }>(
        `/visitas/${encodeURIComponent(id)}`,
        { method: 'PATCH', body: JSON.stringify(req) },
      );
      return resp.data;
    } catch (err) {
      throw this.mapear(err);
    }
  }

  /** POST /visitas/{id}/conferir — marca/desmarca conferida. */
  async conferir(id: string, rowVersion: number): Promise<ConferirResposta> {
    try {
      const resp = await this.client.request<{ data: ConferirResposta }>(
        `/visitas/${encodeURIComponent(id)}/conferir`,
        { method: 'POST', body: JSON.stringify({ row_version: rowVersion }) },
      );
      return resp.data;
    } catch (err) {
      throw this.mapear(err);
    }
  }

  /** GET /visitas/{id}/historico — histórico append-only (leitura universal). */
  async historico(id: string): Promise<readonly HistoricoEntry[]> {
    const resp = await this.client.request<{ data: readonly HistoricoEntry[] }>(
      `/visitas/${encodeURIComponent(id)}/historico`,
    );
    return resp.data;
  }

  /** Traduz ApiError de status conhecido em erro de domínio do M8. */
  private mapear(err: unknown): unknown {
    if (err instanceof ApiError) {
      if (err.status === 409) {
        return new ConflictError(extrairEstadoAtual(err.body));
      }
      if (err.status === 503) {
        return new ServiceUnavailableError();
      }
    }
    return err;
  }
}
